import { useState, useCallback, useRef, useMemo } from 'react'
import {
  ReactFlow,
  MiniMap,
  Controls,
  Background,
  Handle,
  Position,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { ETL_NODE_REGISTRY, generateTemplateNodes } from '../../data/etlNodeRegistry'
import * as LucideIcons from 'lucide-react'
import './EtlCanvas.css'

// ---------------------------------------------------------------------------
// Custom ETL Node component — defined inside EtlCanvas.jsx
// Same structural pattern as WorkflowNode in designer/WorkflowCanvas.jsx
// ---------------------------------------------------------------------------
function EtlNode({ id, data, selected }) {
  const nodeDef = ETL_NODE_REGISTRY[data.nodeType]
  if (!nodeDef) return null

  const IconComponent = LucideIcons[nodeDef.icon] || LucideIcons.Box
  const nodeColor = nodeDef.color || '#8b949e'
  const isSourceOrTarget = nodeDef.category === 'sources' || nodeDef.category === 'targets'

  // Input/output handle definitions from registry
  const inputs = nodeDef.inputs || []
  const outputs = nodeDef.outputs || []

  // Connection info
  const connName = data.connectionSnapshot?.name || null
  const connDriver = data.connectionSnapshot?.driverId || null

  return (
    <div
      className={`etl-node ${data.status || ''} ${selected ? 'selected' : ''}`}
      style={{ '--node-color': nodeColor }}
    >
      {/* Top color bar rendered via ::before in CSS */}

      {/* --- Input handles --- */}
      {inputs.length === 0 && (
        <Handle type="target" position={Position.Left} className="etl-handle" />
      )}
      {inputs.length === 1 && (
        <Handle
          type="target"
          position={Position.Left}
          id={inputs[0].id}
          className="etl-handle"
        />
      )}
      {inputs.length > 1 &&
        inputs.map((inp, idx) => (
          <Handle
            key={inp.id}
            type="target"
            position={Position.Left}
            id={inp.id}
            className="etl-handle"
            style={{
              top: `${((idx + 1) / (inputs.length + 1)) * 100}%`,
            }}
          />
        ))}

      {/* --- Node header --- */}
      <div className="etl-node-header">
        <IconComponent
          size={16}
          style={{ color: nodeColor, flexShrink: 0 }}
        />
        <span className="etl-node-label">
          {data.label || nodeDef.label}
        </span>
        {data.status && (
          <span className={`etl-node-status-icon ${data.status}`} />
        )}
      </div>

      {/* --- Connection indicator (source/target only) --- */}
      {isSourceOrTarget && (
        <div className={`etl-node-connection ${connName ? 'connected' : ''}`}>
          <LucideIcons.Link size={10} />
          <span>{connName || 'No connection'}</span>
        </div>
      )}

      {/* --- Node summary --- */}
      <div className="etl-node-summary">
        {data.status === 'running'
          ? 'Processing...'
          : data.status === 'complete'
          ? data.executionResult?.row_count
            ? `${data.executionResult.row_count} rows`
            : 'Done'
          : data.status === 'error'
          ? 'Error'
          : nodeDef.category}
      </div>

      {/* --- Schema preview tags --- */}
      {data.schemaPreview && (
        <div className="etl-node-schema">
          {data.schemaPreview.slice(0, 3).map((col, i) => (
            <span key={i} className="etl-schema-tag">
              {col}
            </span>
          ))}
          {data.schemaPreview.length > 3 && (
            <span className="etl-schema-more">
              +{data.schemaPreview.length - 3}
            </span>
          )}
        </div>
      )}

      {/* --- Output handles --- */}
      {outputs.length <= 1 && (
        <Handle
          type="source"
          position={Position.Right}
          id={outputs[0]?.id || 'output'}
          className="etl-handle"
        />
      )}
      {outputs.length > 1 &&
        outputs.map((out, idx) => (
          <Handle
            key={out.id}
            type="source"
            position={Position.Right}
            id={out.id}
            className="etl-handle"
            style={{
              top: `${((idx + 1) / (outputs.length + 1)) * 100}%`,
            }}
          />
        ))}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main ETL Canvas component
// ---------------------------------------------------------------------------
function EtlCanvas({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeSelect,
  onNodeAdd,
  selectedNodeId,
  isExecuting,
  onInit,
}) {
  const reactFlowWrapper = useRef(null)
  const [reactFlowInstance, setReactFlowInstance] = useState(null)

  // Memoize nodeTypes so ReactFlow doesn't re-register on every render
  const nodeTypes = useMemo(() => ({ etlNode: EtlNode }), [])

  // Drag-over handler (required for drop to work)
  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  // Drop handler — creates a new node from palette data
  const onDrop = useCallback(
    (event) => {
      event.preventDefault()

      const nodeType = event.dataTransfer.getData('application/etlflow')
      if (!nodeType || !ETL_NODE_REGISTRY[nodeType]) return

      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      })

      // Handle template nodes — expand into multiple pre-wired nodes
      if (ETL_NODE_REGISTRY[nodeType]?.isTemplate) {
        const { nodes: templateNodes, edges: templateEdges } =
          generateTemplateNodes(nodeType, position.x, position.y)

        templateNodes.forEach((node) => onNodeAdd(node))
        templateEdges.forEach((edge) =>
          onEdgesChange([{ type: 'add', item: edge }])
        )
        return
      }

      // Regular node
      const nodeDef = ETL_NODE_REGISTRY[nodeType]
      const newNode = {
        id: `${nodeType}-${Date.now()}`,
        type: 'etlNode',
        position,
        data: {
          nodeType,
          label: nodeDef.label,
          config: { ...nodeDef.defaultConfig },
          enabled: true,
          status: null,
        },
      }
      onNodeAdd(newNode)
    },
    [reactFlowInstance, onNodeAdd, onEdgesChange]
  )

  // Node click — select the node
  const onNodeClick = useCallback(
    (_event, node) => {
      onNodeSelect(node.id)
    },
    [onNodeSelect]
  )

  // Pane click — deselect
  const onPaneClick = useCallback(() => {
    onNodeSelect(null)
  }, [onNodeSelect])

  // Store ReactFlow instance for screenToFlowPosition + pass to parent
  const handleInit = useCallback(
    (instance) => {
      setReactFlowInstance(instance)
      if (onInit) onInit(instance)
    },
    [onInit]
  )

  return (
    <div className="etl-canvas" ref={reactFlowWrapper}>
      <ReactFlow
        nodes={nodes.map((n) => ({
          ...n,
          selected: n.id === selectedNodeId,
        }))}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onInit={handleInit}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        nodesDraggable={!isExecuting}
        nodesConnectable={!isExecuting}
        fitView
        snapToGrid
        snapGrid={[20, 20]}
        defaultEdgeOptions={{ type: 'smoothstep', animated: false }}
        deleteKeyCode={isExecuting ? null : ['Backspace', 'Delete']}
        proOptions={{ hideAttribution: true }}
      >
        <Controls className="etl-controls" />
        <MiniMap
          nodeColor={(node) => {
            const status = node.data?.status
            if (status === 'complete') return '#3fb950'
            if (status === 'running') return '#58a6ff'
            if (status === 'error') return '#f85149'
            return '#30363d'
          }}
          maskColor="rgba(0, 0, 0, 0.8)"
          style={{ backgroundColor: '#0d1117' }}
        />
        <Background variant="dots" gap={20} size={1} color="#21262d" />
      </ReactFlow>

      {/* Empty state overlay */}
      {nodes.length === 0 && (
        <div className="etl-canvas-empty-state">
          <LucideIcons.Workflow size={48} />
          <h3>Design Your ETL Pipeline</h3>
          <p>Drag nodes from the palette to get started</p>
        </div>
      )}
    </div>
  )
}

export default EtlCanvas
